import { tokens } from "@/styles/generated/tokens";
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Clapperboard,
  Film,
  Users,
  Play,
  Library,
  Settings,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores/appStore";
import { useGlobalDragDrop } from "@/hooks/useGlobalDragDrop";
import { GlobalDropOverlay } from "@/components/uploads/GlobalDropOverlay";
import { UploadAssetsModal } from "@/components/uploads/UploadAssetsModal";
import { isSupportedAssetType } from "@/components/uploads/types";
import "./ProjectSidePanel.css";

// Resolved token colours also supply the translucent selected-state channels.
const BRAND_COLORS = {
  berry: tokens.COLOR_PRIMITIVE_PINK_500,
  violet: tokens.COLOR_PRIMITIVE_VIOLET_500, // → --color-primitive-violet-500
  indigo: tokens.COLOR_PRIMITIVE_INDIGO_500, // → --color-primitive-indigo-500
  cobalt: tokens.COLOR_PRIMITIVE_BLUE_500, // Bible Blue
  azure: tokens.COLOR_NAVIGATION_TIMELINE, // Deepest blue in spectrum
  neutral: tokens.COLOR_PRIMITIVE_NEUTRAL_400,
} as const;

interface ProjectNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  getPath: (projectId: string) => string;
  /** Pattern to match for active state */
  matchPattern?: RegExp;
  /** Brand color for this nav item */
  color: string;
}

// Project-scoped navigation items (shown when inside a project)
// Note: "Projects" is in the VerticalNav icon rail, not here
// Order: Shots, Workflows, Cast, Assets, Timeline, [divider], Settings
const MAIN_NAV_ITEMS: ProjectNavItem[] = [
  {
    id: "scenes",
    label: "Scenes",
    icon: Clapperboard,
    getPath: (pid) => `/productions/${pid}/scenes`,
    matchPattern: /^\/productions\/[^/]+\/scenes$/,
    color: BRAND_COLORS.berry,
  },
  {
    id: "workflows",
    label: "Workflows",
    icon: Film,
    getPath: (pid) => `/productions/${pid}/workflows`,
    matchPattern: /^\/productions\/[^/]+\/workflows$/,
    color: BRAND_COLORS.violet,
  },
  {
    id: "cast",
    label: "Cast",
    icon: Users,
    getPath: (pid) => `/productions/${pid}/cast`,
    // Match /productions/:id/cast and /productions/:id/cast/:castId
    matchPattern: /^\/productions\/[^/]+\/cast(\/[^/]+)?$/,
    color: BRAND_COLORS.indigo,
  },
  {
    id: "library",
    label: "Library",
    icon: Library,
    getPath: (pid) => `/productions/${pid}/library`,
    matchPattern: /^\/productions\/[^/]+\/library$/,
    color: BRAND_COLORS.cobalt,
  },
  {
    id: "timeline",
    label: "Timeline",
    icon: Play,
    getPath: (pid) => `/video-editor/${pid}`,
    matchPattern: /^\/video-editor\/[^/]+$/,
    color: BRAND_COLORS.azure,
  },
];

const BOTTOM_NAV_ITEMS: ProjectNavItem[] = [
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    getPath: (pid) => `/productions/${pid}/settings`,
    matchPattern: /^\/productions\/[^/]+\/settings$/,
    color: BRAND_COLORS.neutral,
  },
];

/**
 * ProjectSidePanel - Vertical navigation for production-scoped pages
 * Only shows when a production is selected (inside /productions/:projectId/*)
 * Hidden on /productions (list view), /workflows/* (canvas), /video-editor/* (timeline)
 *
 * Note: The "Studio" entry point is in the VerticalNav icon rail
 */
export function ProjectSidePanel() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProject } = useAppStore();

  // Use URL param first, fall back to store
  const projectId = urlProjectId || currentProject?.id;

  // Track animation state for slide-in effect
  const [isVisible, setIsVisible] = useState(false);
  const prevProjectId = useRef<string | undefined>(undefined);

  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

  // Handle files dropped from global drag-drop
  const handleGlobalDrop = useCallback((files: File[]) => {
    const supportedFiles = files.filter(isSupportedAssetType);
    if (supportedFiles.length > 0) {
      setDroppedFiles(supportedFiles);
      setIsUploadModalOpen(true);
    }
  }, []);

  // Global drag-drop detection (always active when project is selected)
  const { isDragging, resetDragState } = useGlobalDragDrop({
    onDrop: handleGlobalDrop,
    enabled: !!projectId,
  });

  // Handle upload button click
  const handleUploadClick = useCallback(() => {
    setDroppedFiles([]);
    setIsUploadModalOpen(true);
  }, []);

  // Handle modal close
  const handleUploadModalClose = useCallback(() => {
    setIsUploadModalOpen(false);
    setDroppedFiles([]);
  }, []);

  // Trigger slide-in animation when projectId changes from undefined to defined
  useEffect(() => {
    if (projectId && !prevProjectId.current) {
      // Small delay to ensure the initial state is rendered first
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else if (projectId) {
      // Already have a project, show immediately
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
    prevProjectId.current = projectId;
  }, [projectId]);

  const handleNavClick = (item: ProjectNavItem) => {
    if (!projectId) return;
    const path = item.getPath(projectId);
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const isActive = (item: ProjectNavItem): boolean => {
    if (item.matchPattern) {
      return item.matchPattern.test(location.pathname);
    }
    return false;
  };

  // Don't render if no project is selected (user is on /projects list view)
  if (!projectId) {
    return null;
  }

  // Helper to convert hex to RGB for rgba() usage
  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
      return [1, 3, 5]
        .map((offset) =>
          parseInt(BRAND_COLORS.berry.slice(offset, offset + 2), 16),
        )
        .join(", ");
    }
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  };

  const renderNavItem = (item: ProjectNavItem) => {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item)}
        className={cn(
          "project-side-panel__button",
          active && "project-side-panel__button--active",
        )}
        style={
          {
            "--nav-color": item.color,
            "--nav-color-rgb": hexToRgb(item.color),
          } as React.CSSProperties
        }
      >
        <Icon className="w-4 h-4" />
        {item.label}
      </button>
    );
  };

  return (
    <>
      <nav
        className={cn(
          "project-side-panel",
          isVisible
            ? "project-side-panel--visible"
            : "project-side-panel--initial",
        )}
      >
        {/* Main navigation items */}
        {MAIN_NAV_ITEMS.map(renderNavItem)}

        {/* Divider */}
        <div className="project-side-panel__divider" />

        {/* Bottom navigation items (Settings) */}
        {BOTTOM_NAV_ITEMS.map(renderNavItem)}

        {/* Upload assets button - action, not navigation */}
        <button
          onClick={handleUploadClick}
          className="project-side-panel__button project-side-panel__upload-button"
          style={
            {
              "--nav-color": BRAND_COLORS.cobalt,
              "--nav-color-rgb": hexToRgb(BRAND_COLORS.cobalt),
            } as React.CSSProperties
          }
        >
          <Upload className="w-4 h-4" />
          Upload assets
        </button>
      </nav>

      {/* Global drop overlay - shown when dragging files over window */}
      <GlobalDropOverlay
        isVisible={isDragging}
        onDrop={handleGlobalDrop}
        onDragEnd={resetDragState}
      />

      {/* Upload modal */}
      {projectId && (
        <UploadAssetsModal
          isOpen={isUploadModalOpen}
          onClose={handleUploadModalClose}
          projectId={projectId}
          initialFiles={droppedFiles}
        />
      )}
    </>
  );
}
